import { ArrowRight, ArrowDown, Check, X } from "lucide-react";
import { Kicker, Big, Lines, FlowV, Stack } from "./ui";
import { FreeVideo } from "./VideoSection";
import { Cta, Micro } from "./cta";

const Math100k = ({ children, light, id }) => <p className={`math${light ? " light" : ""}`} data-testid={id}>{children}</p>;

/* 1 — HERO + FREE VIDEO */
const FLOW = ["Idea", "Method", "$2K–$10K offer", "Speak your book in 3 hours", "Video", "AI sales system", "Consistent cash"];
export const Hero = () => (
  <header id="top" className="sh" data-testid="hero-section">
    <div className="lux" data-testid="hero-lux">
      <span className="lux-rule" aria-hidden="true" />
      <h1 className="lux-h1" data-testid="hero-title">
        <span>What if your <em className="lux-wtf">WTF</em> idea</span>
        <span>is the path to your <span className="gold-text">$100K business</span></span>
        <span>+ extraordinary life?</span>
      </h1>
      <ol className="lux-flow" data-testid="hero-flow">
        {FLOW.map((t, i) => [i === 4 && <li key="br" className="lux-br" aria-hidden="true" />, <li key={t}>{i > 0 && <ArrowRight size={14} strokeWidth={3} aria-hidden="true" className="lux-arr" />}<span className="lux-pill">{t}</span></li>])}
      </ol>
      <p className="lux-sub" data-testid="hero-subhead">The purpose, bestseller, dream clients, dream relationship, fully expressed free spirited life — starts when you say <b className="gold-text">YES</b> — and lead one idea to the finish line, even when it makes absolutely no sense to anyone but you.</p>
      <span className="lux-rule" aria-hidden="true" />
    </div>
    <div id="video" className="hero-video"><FreeVideo /></div>
    <Micro>FREE • THE EXACT MODEL • NO 47-HOUR COURSE</Micro>
  </header>
);

/* 4 — THE BIG IDEA */
const CHAIN = [["Your WTF idea becomes your ", "message."], ["Your message becomes your ", "method."], ["Your method becomes your ", "intellectual property."], ["Your intellectual property becomes your ", "book."], ["Your book builds ", "authority."], ["Your authority supports a ", "high-value offer."], ["Your AI-powered Freedom Funnel gives all of it ", "one destination."]];

export const BigIdea = () => (
  <section className="sec narrow center" data-testid="big-idea-section">
    <Big className="huge">Your WTF idea<br /><span className="gold-text">is the business.</span></Big>
    <Lines items={["You don’t need 40 disconnected ideas.", "You need one belief you believe deeply enough to build a world around."]} />
    <div className="lines chain">{CHAIN.map(([a, b]) => <p key={b}>{a}<b>{b}</b></p>)}</div>
    <FlowV id="big-idea-flow" items={["WTF IDEA", "UNIQUE METHOD", "AUTHORITY-BUILDING BOOK", "$2K–$10K+ OFFER", "AI-POWERED FREEDOM FUNNEL", "CONSISTENT CASH"]} />
    <img className="wide-img" src="/img/wtf-ideas.webp" alt="40 WTF ideas, from Good Girl Funeral to Wounded Woman To Wonder Woman, and the 4 modules: WTF idea for perfect client, custom framework, ascension plan with AI, sell with stories" loading="lazy" data-testid="wtf-ideas-image" />
    <Big as="p" className="mt">One idea.<br /><span className="gold-text">A complete business.</span></Big>
  </section>
);

/* 5 — THE $100K MODEL */
export const Model100k = () => (
  <section className="dark-band" data-testid="model-100k-section">
    <div className="sec narrow center">
      <Kicker light>A different way to think about $100K</Kicker>
      <Big className="light">What if you didn’t need 1,000 customers?</Big>
      <Math100k light id="model-math">10 <i>×</i> $10K <i>=</i> <span className="gold-text">$100K</span></Math100k>
      <Lines className="light" items={["I would rather help you build something valuable enough for 10 dream clients than build a business that constantly needs hundreds or thousands of random buyers."]} />
      <Lines className="light q" items={["You don’t need millions of views.", "You don’t need to go viral.", "You don’t need another cheap offer every month."]} />
      <Lines className="light strong" items={["You need to become extremely valuable to the right people."]} />
      <div className="lines light strong mt-s">
        <p>Your book isn’t there to make you famous.</p>
        <p>Your content isn’t there to make you an influencer.</p>
        <p>Your funnel isn’t there to collect random leads.</p>
      </div>
      <Lines className="light" items={["Every piece has one job:"]} />
      <Big as="p" className="light"><span className="gold-text">Move the right person deeper into your world.</span></Big>
      <p className="disclaimer light-d">10 × $10K illustrates a business model, not guaranteed earnings. Client acquisition, pricing and results vary.</p>
    </div>
  </section>
);

/* 7 — THE BOOK */
export const TheBook = () => (
  <section className="sec narrow center" data-testid="book-section">
    <Kicker>Your authority engine</Kicker>
    <Big>Your book isn’t the end product.<br /><span className="gold-text">It’s the front door.</span></Big>
    <Lines items={["I don’t care about helping you publish a book just so you can put “author” in your bio.", "I want your book to DO something."]} />
    <Lines className="q" items={["Your book gives your belief somewhere to live.", "It establishes your philosophy.", "It explains your method.", "It demonstrates your expertise.", "It starts conversations.", "It builds trust before you ever get on a sales call."]} />
    <Lines items={["And it naturally leads the right person toward the deeper transformation you sell."]} />
    <Stack items={["THE BOOK BUILDS AUTHORITY.", "THE OFFER MONETIZES THE TRANSFORMATION.", "THE SYSTEM CONNECTS EVERYTHING."]} />
    <div className="books four mt-s" data-testid="books-gallery">
      {["voice", "monogamy", "quantum", "freedom"].map((b) => <img key={b} src={`/img/tm-${b}.webp`} alt="Client book cover" loading="lazy" />)}
    </div>
  </section>
);

/* 9 — OLD WAY VS NEW WAY */
const OLD = ["More content.", "More offers.", "More launches.", "More niches.", "More followers.", "More funnels.", "More courses.", "More trying to sound like everyone else."];
const NEW = ["One person.", "One belief shift.", "One WTF idea.", "One method.", "One authority-building book.", "One high-value transformation.", "One offer ecosystem.", "One AI-powered Freedom Funnel.", "One message you become known for."];

export const OldNew = () => (
  <section className="sec" data-testid="old-new-section">
    <Big className="center-t">You don’t need more.</Big>
    <div className="compare">
      <div className="cmp old" data-testid="old-way"><p className="cmp-h">Old way</p>{OLD.map((t) => <p key={t}><X size={16} /> {t}</p>)}</div>
      <div className="cmp new" data-testid="new-way"><p className="cmp-h">New way</p>{NEW.map((t) => <p key={t}><Check size={16} /> {t}</p>)}</div>
    </div>
    <Big as="p" className="center-t mt">You don’t need 40.<br /><span className="gold-text">You need the right one.</span></Big>
  </section>
);

/* 12 — THE SCOREBOARD */
const NOT = ["“I watched every module.”", "“I got more followers.”", "“I created another PDF.”", "“I bought another course.”"];
const BUT = ["“I know what I stand for.”", "“I created my method.”", "“I published my book.”", "“I built my high-value offer.”", "“I created client results.”", "“I built the system behind it.”", "“I signed the right clients.”"];

export const Scoreboard = () => (
  <section className="sec narrow center" data-testid="scoreboard-section">
    <Big>These are the stories I care about.</Big>
    <div className="notbut">
      <div className="nb-not" data-testid="scoreboard-not"><p className="cmp-h">Not</p>{NOT.map((t) => <p key={t}>{t}</p>)}</div>
      <ArrowDown size={22} className="nb-arrow" aria-hidden="true" />
      <div className="nb-but" data-testid="scoreboard-but"><p className="cmp-h">But</p>{BUT.map((t) => <p key={t}>{t}</p>)}</div>
    </div>
    <Big as="p" className="mt"><span className="gold-text">That’s the scoreboard.</span></Big>
  </section>
);

/* 13 — WHO THIS IS FOR */
const FOR = ["People with ideas worth spreading.", "Coaches, experts, creators and thought leaders willing to become known for something.", "People who want their book connected to a real transformation.", "People who want clients — not just followers.", "People who want their content, book, offers and systems reinforcing ONE message.", "People tired of constantly wondering what to post or sell next.", "People who want a business that supports their freedom instead of consuming it."];

export const WhoFor = () => (
  <section className="sec split" data-testid="who-for-section">
    <img className="portrait" src="/img/dasha-hero.webp" alt="Dasha Love" loading="lazy" />
    <div>
      <Big>I’m not trying to build a factory of course collectors.</Big>
      <Lines items={["I’d rather help a smaller number of people build something valuable.", "This is for:"]} />
      <div className="incl plain">{FOR.map((t) => <p key={t}><Check size={16} /> {t}</p>)}</div>
      <Lines className="mt-s" items={["If you’re looking for another giant course to collect..."]} />
      <Lines className="strong" items={["This probably isn’t your world."]} />
      <Lines items={["If you’re ready to take ONE idea seriously enough to build an entire business around it..."]} />
      <Big as="p"><span className="gold-text">Keep going.</span></Big>
    </div>
  </section>
);

/* 16 — FINAL CLOSE */
export const FinalClose = () => (
  <section className="dark-band" data-testid="final-close-section">
    <div className="sec narrow center">
      <Kicker light>Live a NO BS life.</Kicker>
      <Big className="light">You don’t need another business idea.<br /><span className="gold-text">You need to go deeper into yours.</span></Big>
      <Big as="p" className="light mt">10 incredible clients can matter more than <span className="gold-text">10,000 random followers.</span></Big>
      <div className="lines light strong mt-s">
        <p>Be fully expressed.</p>
        <p>Become known for something.</p>
        <p>Build the system around it.</p>
        <p>Create consistent cash.</p>
      </div>
      <div className="cta-pair"><Cta id="final-cta" /></div>
    </div>
  </section>
);
